import React from "react";

const OptimumCheckoutPrivacyPolicy = () => {
  return (
    <div className="container mx-auto px-4 py-40">
      <h1 className="text-3xl font-bold mb-6">
        Privacy Policy for Optimum Checkout
      </h1>

      <p className="mb-2">
        <strong>Effective Date:</strong> June 19, 2026
      </p>

      <p className="mb-6">
        <strong>Last Updated:</strong> June 19, 2026
      </p>

      <p className="mb-6">
        Optimum Checkout is operated by Mean3 Pvt Ltd (“Mean3,” “we,” “us,” or
        “our”). This Privacy Policy explains how information is handled when
        Shopify merchants install or use the Optimum Checkout application
        (“App”).
      </p>

      <h2 className="text-2xl font-semibold mb-4">1. About Optimum Checkout</h2>

      <p className="mb-3">
        Optimum Checkout helps Shopify merchants customize their cart and
        checkout experience. Its features may include:
      </p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>Hiding, showing, reordering, or renaming payment methods</li>
        <li>Hiding, showing, reordering, or renaming shipping methods</li>
        <li>Creating country- and city-based checkout rules</li>
        <li>Adding cart and checkout upsell offers</li>
        <li>Configuring checkout field validations</li>
        <li>Adding custom checkout fields</li>
        <li>Managing delivery city options</li>
        <li>Displaying checkout banners, badges, and custom styles</li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">
        2. Customer Personal Data
      </h2>

      <p className="mb-3">
        Optimum Checkout does not collect, transmit to Mean3, store, sell, or
        retain customer personal information.
      </p>

      <p className="mb-3">The App does not store information such as:</p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Customer names</li>
        <li>Email addresses</li>
        <li>Phone numbers</li>
        <li>Billing addresses</li>
        <li>Shipping addresses</li>
        <li>Payment card information</li>
        <li>Order information</li>
        <li>Customer account information</li>
        <li>Information entered into checkout fields</li>
      </ul>

      <p className="mb-6">
        Checkout conditions are applied within Shopify’s environment using the
        checkout information made available by Shopify. This information is not
        transferred to or stored in a Mean3 database.
      </p>

      <p className="mb-6">
        Payment information is processed directly by Shopify or the merchant’s
        selected payment provider. Optimum Checkout does not receive or store
        complete payment card numbers, card security codes, or banking
        credentials.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        3. Merchant and Store Data
      </h2>

      <p className="mb-3">
        Mean3 does not maintain an external database containing merchant or
        Shopify store data.
      </p>

      <p className="mb-3">
        App settings and checkout configurations are stored within Shopify using
        Shopify metafields. Depending on the features configured by the
        merchant, these settings may include:
      </p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>Payment method rules</li>
        <li>Shipping method rules</li>
        <li>Country and city conditions</li>
        <li>Checkout validation settings</li>
        <li>Custom field settings</li>
        <li>Upsell and cross-sell configurations</li>
        <li>Selected product, variant, or collection identifiers</li>
        <li>Banner headings and descriptions</li>
        <li>Checkout badge and styling preferences</li>
        <li>Rule activation status</li>
      </ul>

      <p className="mb-6">
        This information remains within the Shopify platform and is governed by
        Shopify’s privacy and security practices.
      </p>

      <h2 className="text-2xl font-semibold mb-4">4. City Reference Data</h2>

      <p className="mb-6">
        Optimum Checkout uses GitHub to maintain general country and city
        reference data used by the city-selector feature. This reference data
        contains general geographic names only. It does not contain merchant
        information, customer information, checkout information, addresses, or
        other personal data.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        5. How Information Is Used
      </h2>

      <p className="mb-3">
        Optimum Checkout uses Shopify-hosted configuration data only to provide
        the features selected by the merchant, including:
      </p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>Applying payment and shipping rules</li>
        <li>Applying country- and city-based conditions</li>
        <li>Displaying cart or checkout upsells</li>
        <li>Validating required checkout fields</li>
        <li>Displaying custom checkout content</li>
        <li>Managing checkout city options</li>
        <li>Displaying banners, badges, and styles</li>
        <li>Activating, editing, or deleting checkout rules</li>
      </ul>

      <p className="mb-6">
        We do not use merchant or customer information for advertising,
        profiling, marketing, or unrelated commercial purposes.
      </p>

      <h2 className="text-2xl font-semibold mb-4">6. Data Storage</h2>

      <p className="mb-3">
        Optimum Checkout does not operate an external database for storing
        merchant or customer data.
      </p>

      <p className="mb-3">App-related information is handled as follows:</p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>Checkout settings and rules are stored in Shopify metafields.</li>
        <li>General city reference data is stored on GitHub.</li>
        <li>The App is hosted on a Namecheap VPS located in the United States.</li>
        <li>Customer personal data is not stored on the Namecheap VPS.</li>
        <li>Merchant configuration data is not copied into a Mean3 database.</li>
        <li>
          Limited temporary authentication or session information may be used to
          securely operate the App within Shopify Admin.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">
        7. Data Retention and App Uninstallation
      </h2>

      <p className="mb-6">
        Because Mean3 does not maintain an external database for Optimum
        Checkout, Mean3 does not retain merchant or customer data after the App
        is uninstalled.
      </p>

      <p className="mb-6">
        Information stored within Shopify, including Shopify metafields,
        remains subject to Shopify’s platform behavior, the merchant’s Shopify
        account settings, and Shopify’s applicable privacy policies.
      </p>

      <p className="mb-6">
        General city reference data stored on GitHub is not merchant-specific
        or customer-specific and may continue to be maintained after an
        individual merchant uninstalls the App.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        8. Cookies and Tracking Technologies
      </h2>

      <p className="mb-3">
        Optimum Checkout uses only cookies or similar technologies that are
        essential for:
      </p>

      <ul className="list-disc list-inside mb-4 ml-4">
        <li>Secure authentication</li>
        <li>Maintaining an active session</li>
        <li>Preventing unauthorized access</li>
        <li>Operating the App within Shopify Admin</li>
        <li>Maintaining essential App functionality</li>
      </ul>

      <p className="mb-6">
        The App does not use non-essential cookies, behavioural tracking
        cookies, advertising cookies, or third-party marketing cookies. The App
        does not use Google Analytics, Meta Pixel, or similar advertising and
        behavioural analytics tools.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        9. Third-Party Service Providers
      </h2>

      <h3 className="text-xl font-medium mb-2">Shopify</h3>
      <p className="mb-4">
        Shopify provides the platform through which the App is installed and
        operated. Shopify metafields are used to store merchant-created App
        settings and checkout rules.
      </p>

      <h3 className="text-xl font-medium mb-2">Namecheap</h3>
      <p className="mb-4">
        Namecheap provides the VPS infrastructure used to host the App. The
        hosting server is located in the United States.
      </p>

      <h3 className="text-xl font-medium mb-2">GitHub</h3>
      <p className="mb-6">
        GitHub is used to store and maintain general country and city reference
        data. This data is not personal and is not linked to any merchant or
        customer.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        10. Data Sharing and Sale
      </h2>

      <p className="mb-3">
        Mean3 does not sell, rent, trade, or share merchant or customer
        personal information for advertising or marketing purposes.
      </p>

      <p className="mb-3">
        Information may only be disclosed when legally required to:
      </p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>Comply with an applicable law or regulation</li>
        <li>Respond to a valid court order or government request</li>
        <li>Protect the security and integrity of the App</li>
        <li>Prevent fraud, abuse, or unauthorized access</li>
        <li>
          Protect the legal rights of Mean3, Shopify merchants, or other parties
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">
        11. Shopify Privacy and Compliance Requests
      </h2>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>customers/data_request</li>
        <li>customers/redact</li>
        <li>shop/redact</li>
      </ul>

      <p className="mb-6">
        Because Optimum Checkout does not store customer personal data or
        maintain an external merchant database, these requests generally confirm
        that no relevant personal data is held by Mean3.
      </p>

      <h2 className="text-2xl font-semibold mb-4">12. Data Security</h2>

      <p className="mb-3">
        Mean3 uses reasonable technical and organizational measures designed to
        protect the App and prevent unauthorized access, misuse, alteration, or
        disclosure.
      </p>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>Secure HTTPS connections</li>
        <li>Shopify authentication</li>
        <li>Access controls</li>
        <li>Essential session management</li>
        <li>Restricted administrative access</li>
        <li>Server and application security controls</li>
        <li>Regular software and security updates</li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">
        13. Merchant Responsibilities
      </h2>

      <ul className="list-disc list-inside mb-6 ml-4">
        <li>Maintaining an accurate privacy policy for their own store</li>
        <li>Ensuring their checkout rules comply with applicable laws</li>
        <li>Providing customers with any legally required notices</li>
        <li>
          Ensuring custom checkout content is accurate and not misleading
        </li>
        <li>Avoiding the collection of unnecessary or sensitive information</li>
        <li>Managing staff access to the Shopify store and the App</li>
        <li>
          Responding to customer privacy requests relating to the merchant’s own
          store
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mb-4">
        14. Children’s Privacy
      </h2>

      <p className="mb-6">
        Optimum Checkout is intended for use by Shopify merchants and is not
        directed toward children. Mean3 does not knowingly collect or store
        personal information from children through the App.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        15. International Hosting
      </h2>

      <p className="mb-6">
        The App is hosted on a Namecheap VPS located in the United States.
        However, Optimum Checkout does not intentionally transfer or store
        customer personal information on this server.
      </p>

      <h2 className="text-2xl font-semibold mb-4">
        16. Changes to This Privacy Policy
      </h2>

      <p className="mb-6">
        We may update this Privacy Policy when necessary to reflect changes to
        App functionality, data-handling practices, service providers, legal or
        regulatory requirements, and Shopify platform updates.
      </p>

      <h2 className="text-2xl font-semibold mb-4">17. Contact Us</h2>

      <p className="mb-2">
        For questions, concerns, or privacy-related requests regarding Optimum
        Checkout, contact:
      </p>

      <ul className="list-disc list-inside ml-4">
        <li>Mean3 Pvt Ltd</li>
        <li>447 Broadway, 2nd Floor, Suite #670</li>
        <li>New York, NY 10013</li>
        <li>United States</li>
        <li>
          Email:{" "}
          <a
            href="mailto:support@mean3.com"
            className="text-blue-500 hover:underline"
          >
            support@mean3.com
          </a>
        </li>
      </ul>

      <p className="mt-6">
        When submitting a privacy request, please include the relevant Shopify
        store URL and a clear description of your request.
      </p>
    </div>
  );
};

export default OptimumCheckoutPrivacyPolicy;